import { Injectable, Injector, SimpleChanges, ChangeDetectorRef } from "@angular/core";

/**
 * based on discussion in https://medium.com/@laidav/angular-mixins-a-reusability-technique-138776bc2b78#:~:text=What%20are%20mixins%20in%20TypeScript%3F%20They%20are%20functions,together%20so%20we%20can%20share%20functionality%20across%20classes.;
 *
 * Concrete component should implement from { OnChanges, OnInit, DoCheck, AfterContentInit, AfterContentChecked, AfterViewInit, AfterViewChecked, OnDestroy
 */
@Injectable()
export abstract class BaseComponentMixin {
  constructor(public injector: Injector) {}

  ngOnChanges?(changes: SimpleChanges): void;

  ngOnInit?(): void;
  ngDoCheck?(): void;
  ngAfterContentInit?(): void;
  ngAfterContentChecked?(): void;
  ngAfterViewInit?(): void;
  ngAfterViewChecked?(): void;
  ngOnDestroy?(): void;
}

/**
 * based on discussion in https://medium.com/@laidav/angular-mixins-a-reusability-technique-138776bc2b78#:~:text=What%20are%20mixins%20in%20TypeScript%3F%20They%20are%20functions,together%20so%20we%20can%20share%20functionality%20across%20classes.;
 *
 * Concrete directive should implement from { OnInit, AfterContentInit, AfterContentChecked, AfterViewInit, AfterViewChecked, OnDestroy
 */
@Injectable()
export abstract class BaseDirectiveMixin {
  private __directive_cd: ChangeDetectorRef;

  constructor(public injector: Injector) {
    this.__directive_cd = injector.get(ChangeDetectorRef);
  }
  ngOnInit?(): void;
  ngAfterContentInit?(): void;
  ngAfterContentChecked?(): void;
  ngAfterViewInit?(): void;
  ngAfterViewChecked?(): void;
  ngOnDestroy?(): void;

  /**
   * needs to be overridden with @Input("nameDirective") matching directive
   * selector [nameDirective] & exportAs "nameDirective"
   *
   * this allows an @Input() from a mixin in directive, to take value from host component change
   */
  directiveInput?: Record<string, any>;

  public applyDirectiveInput(value: Record<string, any>, emit = false): void {
    for (let key of Object.keys(value)) {
      if (this[key] !== value[key]) {
        this[key] = value[key];
        if (emit && `${key}Changed` in this) {
          this[`${key}Changed`].emit(this[key]);
        }
      }
    }
  }

  protected _previousValuesMap: Map<string, Record<string, any>> = new Map();
  protected _previousValuesTouched: boolean = false;

  directiveHost?(_: Record<string, any>): Record<string, any>;
}

@Injectable()
export abstract class BaseInjectableMixin {
  constructor(public injector: Injector) {}
}

type MixinConstructor<T = Record<string, never>> = new (...args: any[]) => T;

export type MixinInjectorConstructor = MixinConstructor<BaseComponentMixin>;

export class ComponentMixin extends BaseComponentMixin {}
export class DirectiveMixin extends BaseDirectiveMixin {}
export class InjectableMixin extends BaseInjectableMixin {}
